// studioConversation — which conversation the Studio's chat shows (#404,
// #431), pure.
//
// A project has several conversations; the Studio has one chat, showing one
// of them. The chat panel holds one docked conversation for the whole app
// (chatDock.ts); the Studio shows it only when it is the open project's: a
// durable one that belongs to the project, or a nascent one (not sent yet),
// which becomes the project's with its first message. In every other case
// (another project's, one that belongs to no project, or the list not read
// yet) the Studio shows a FRESH conversation: new, empty and typeable at
// once. It has no id and no record until the Author types (the composer then
// mints it, StudioRight.tsx), so it is never listed and never stored. There
// is no "no conversation" case: the chat always has one to type in.
//
// Which conversation is open is per-device view state: the docked
// conversation rides the pane tree across a reload (viewState.ts). Opening a
// project docks its most recently active conversation, or a new one
// (`conversationToOpenIn`), so switching projects never carries a
// conversation across.

export { conversationToOpenIn, projectRows, studioConversationOf };

type Summary = {
    readonly id: string;
    readonly projectId?: string;
    readonly lastActivityAt?: string;
    readonly archived?: boolean;
};

type OpenConversation = {
    readonly type: "open";
    readonly conversationId: string;
    // Started durably (it has a summary) or still nascent.
    readonly durable: boolean;
};
// A new, empty conversation, not minted yet.
type FreshConversation = { readonly type: "fresh" };
type StudioConversation = OpenConversation | FreshConversation;

const fresh: FreshConversation = { type: "fresh" };

const studioConversationOf = (
    projectId: string,
    docked: string,
    summaries: readonly Summary[] | null,
): StudioConversation => {
    // Whose the docked one is is unknown until the list is read.
    if (summaries === null) return fresh;
    const summary = summaries.find((c) => c.id === docked);
    if (summary === undefined)
        return { type: "open", conversationId: docked, durable: false };
    return summary.projectId === projectId
        ? { type: "open", conversationId: docked, durable: true }
        : fresh;
};

// The project's conversations, the archived ones left out (the picker's
// default list).
const projectRows = <T extends Summary>(
    summaries: readonly T[],
    projectId: string,
): readonly T[] =>
    summaries.filter((c) => c.projectId === projectId && c.archived !== true);

// The conversation to dock on opening `projectId`: its most recently active
// one, else `fresh` (a new, nascent one).
const conversationToOpenIn = (
    summaries: readonly Summary[],
    projectId: string,
    fresh: string,
): { readonly conversationId: string; readonly nascent: boolean } => {
    const newest = [...projectRows(summaries, projectId)].sort((a, b) =>
        (b.lastActivityAt ?? "").localeCompare(a.lastActivityAt ?? ""),
    )[0];
    return newest === undefined
        ? { conversationId: fresh, nascent: true }
        : { conversationId: newest.id, nascent: false };
};
